/**
 * PlaybookListPage — grid of PlaybookCard at /playbooks.
 *
 * Phase C Workstream 3 §3.2 — top-level list view of every playbook
 * with a published bundle. Search filters by playbook_id (the most
 * meaningful field on the list page). Empty state shown when the
 * provider has no loaded bundles.
 */

import { useEffect, useMemo, useState } from 'react';
import { Search, AlertCircle, BookOpen } from 'lucide-react';
import { usePlaybooksStore } from '@/stores/playbooks';
import { PlaybookCard } from './components/PlaybookCard';

export function PlaybookListPage() {
  const list = usePlaybooksStore((s) => s.list);
  const loading = usePlaybooksStore((s) => s.listLoading);
  const error = usePlaybooksStore((s) => s.listError);
  const fetchList = usePlaybooksStore((s) => s.fetchList);

  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return list;
    return list.filter((p) => p.playbook_id.toLowerCase().includes(s));
  }, [list, search]);

  return (
    <div className="px-6 py-6">
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-brand-teal" />
            <h1 className="text-xl font-semibold text-slate-800">Playbooks</h1>
          </div>
          <p className="mt-1 text-sm text-slate-600 max-w-2xl">
            Active Creative Memory playbooks. Each playbook captures the
            organizational consensus on what works (DO rules), what doesn't
            (DON'T rules), and the exemplar creatives that demonstrate the
            patterns. Click into a playbook to inspect the rules and trigger
            a fresh garden cycle.
          </p>
        </div>

        <div className="relative w-72 shrink-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by playbook_id…"
            className="w-full rounded-md border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-brand-teal focus:outline-none focus:ring-1 focus:ring-brand-teal/40"
          />
        </div>
      </header>

      {error && (
        <div className="mb-6 flex items-start gap-3 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
          <AlertCircle className="mt-0.5 w-4 h-4 shrink-0" />
          <div>
            <p className="font-semibold">Failed to load playbooks</p>
            <p className="mt-0.5 text-rose-700">{error}</p>
            <button
              onClick={() => fetchList()}
              className="mt-2 text-xs font-medium underline hover:text-rose-900"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {loading && list.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white p-12 text-center text-sm text-slate-500">
          Loading playbooks…
        </div>
      )}

      {!loading && filtered.length === 0 && !error && (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white p-12 text-center">
          <BookOpen className="mx-auto w-8 h-8 text-slate-300" />
          {list.length === 0 ? (
            <>
              <p className="mt-3 text-sm text-slate-700 font-medium">
                No playbooks loaded yet
              </p>
              <p className="mt-1.5 text-xs text-slate-500 max-w-md mx-auto">
                Run a Creative Memory cycle to publish your first bundle, or
                preload a playbook via the <code>CREATIVE_MEMORY_PLAYBOOK_IDS</code>{' '}
                env var. New playbooks lazy-load when first accessed.
              </p>
            </>
          ) : (
            <p className="mt-3 text-sm text-slate-500">
              No playbooks match "{search}".
            </p>
          )}
        </div>
      )}

      {filtered.length > 0 && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {filtered.map((p) => (
            <PlaybookCard key={p.playbook_id} playbook={p} />
          ))}
        </div>
      )}
    </div>
  );
}
